import _ from 'lodash';
import {AllKeysOf, AllValuesOf, zodEnum, keys, StrictExclude} from './util';
import {
  BusinessAudienceLabels,
  BusinessAudiences,
  PrimaryBusinessCategories,
  PrimaryBusinessCategoryLabels,
  SecondaryBusinessCategories,
  SecondaryBusinessCategoryLabels,
} from './account';
import {typeguardFromArray} from './util';
import {z} from 'zod';
import {SignalDefinition} from './signalDefinition';
import {WappAutoSignalLabels} from './wappStackSignals';

// Signal status - describes the state of scoring data for an account
export const SignalStatusValues = [
  'ok',
  'no_info',
  'no_domain_info',
  'no_company_info',
] as const;
export const SignalStatusSchema = z.enum(SignalStatusValues);
export type SignalStatus = z.infer<typeof SignalStatusSchema>;

export const ManualStackFitLabels = {
  silodTech: "Silo'd Tech",

  usingB2bMarketingAutomation: 'Using B2B Marketing Automation',
  usingCustomerSupport: 'Using Customer Support',
  usingECommerce: 'Using E-Commerce',
  usingModernDataStack: 'Using Modern Data Stack',
  usingMultipleWebAnalytics: 'Using Multiple Web Analytics',

  usingDigitalAds: 'Using Digital Ads',
  usingMultipleDigitalAds: 'Using Multiple Digital Ads',

  usingAi: 'Using AI',
  usingComputerVision: 'Using Computer Vision',
  computerVisionBriaCustom: 'Jobs mention Computer Vision (Bria)',
  usingGenerativeAi: 'Using Gen AI',
  usingMachineLearning: 'Using Machine Learning',

  usingFieldServiceManagement: 'Using Field Service Management',

  usingPublicCloud: 'Using Public Cloud',
  usingAws: 'AWS',
  usingAzure: 'Microsoft Azure',
  usingGcp: 'GCP',

  usingCiCd: 'Using CI/CD',
  circleCi: 'CircleCI',
  jenkins: 'Jenkins',
  teamCity: 'TeamCity',
  travisCi: 'Travis CI',

  cortex: 'Palo Alto Cortex',
  crowdstrike: 'CrowdStrike',
  cyberark: 'CyberArk',
  intune: 'Microsoft Intune',
  microsoftSentinel: 'Microsoft Sentinel',
  paloAltoNetworks: 'Palo Alto Networks',
  pingIdentity: 'Ping Identity',
  sentinelone: 'SentinelOne',
  siem: 'SIEM',
  soar: 'SOAR',

  usingAdobeStack: 'Using Adobe Stack',

  ansible: 'Ansible',
  bitbucket: 'Atlassian Bitbucket',
  buildkite: 'Buildkite',
  github: 'GitHub',
  gitlab: 'GitLab',
  jfrog: 'jFrog',
  puppet: 'Puppet',
  usingPulumi: 'Pulumi',
  usingTerraform: 'Terraform',

  apiiro: 'Apiiro',
  blackDuck: 'Black Duck',
  checkmarx: 'Checkmarx',
  codeQL: 'CodeQL',
  coverity: 'Coverity',
  grype: 'Grype',
  snyk: 'Snyk',
  trivy: 'Trivy',
  veracode: 'Veracode',
  wiz: 'Wiz',

  citrix: 'Citrix',

  usingAbTasty: 'AB Tasty',
  usingLaunchDarkly: 'LaunchDarkly',
  usingOptimizely: 'Optimizely',
  usingSplitIo: 'Split.io',
  usingStatsig: 'Statsig',

  usingSfdcServiceCloud: 'SFDC Service Cloud',
  usingZendesk: 'Zendesk',
  newlyUsingZendesk: 'Newly Using Zendesk',

  usingAts: 'Using ATS',
  noAtsFound: 'No ATS Found',
  unknownAts: 'Has Unknown ATS',
  adpAts: 'ADP ATS',
  angellistAts: 'AngelList ATS',
  usingAshby: 'Ashby',
  bambooHr: 'Bamboo HR',
  breezyHr: 'Breezy HR',
  careerbuilder: 'CareerBuilder',
  clearCompany: 'ClearCompany',
  comparably: 'Comparably',
  eightfold: 'Eightfold',
  gemAts: 'Gem',
  usingGreenhouse: 'Greenhouse',
  usingIcims: 'iCIMS',
  isolved: 'iSolved',
  jazzHr: 'JazzHR',
  usingJobvite: 'Jobvite',
  usingLever: 'Lever',
  linkedInAts: 'LinkedIn ATS',
  paychex: 'Paychex',
  paycom: 'Paycom',
  paycor: 'Paycor',
  paylocity: 'Paylocity',
  personio: 'Personio',
  usingRecruitee: 'Recruitee',
  recruiterflow: 'Recruiterflow ATS',
  ripplingAts: 'Rippling ATS',
  usingSmartRecruiters: 'SmartRecruiters',
  sparkHire: 'Spark Hire',
  talentEd: 'TalentEd',
  taleo: 'Taleo',
  teamtailor: 'Teamtailor',
  ultipro: 'UltiPro ATS',
  usingWorkable: 'Workable',
  usingWorkdayAts: 'Workday ATS',
  yCombinatorAts: 'Y Combinator ATS',

  usingChatGpt: 'ChatGPT',
  usingOpenAi: 'Open AI',
  usingPromptEngineering: 'Prompt Engineering',

  dialogflow: 'Dialogflow',
  kubeFlow: 'KubeFlow',
  usingPytorch: 'PyTorch',
  tensorFlow: 'TensorFlow',

  starburst: 'Starburst',

  alation: 'Alation',
  atlan: 'Atlan',
  collibra: 'Collibra',

  usingDataWarehouse: 'Using Data Warehouse',
  usingAzureSynapse: 'Azure Synapse',
  usingBigQuery: 'Big Query',
  usingDatabricks: 'Databricks',
  usingOracleAutonomousDataWarehouse: 'Oracle Autonomous Data Warehouse',
  usingPanoply: 'Panoply',
  usingRedshift: 'Redshift',
  usingSapDataWarehouseCloud: 'SAP Data Warehouse Cloud',
  usingSnowflake: 'Snowflake',
  usingTeradataVantage: 'Teradata Vantage',
  usingVertica: 'Vertica',
  usingYellowbrick: 'Yellowbrick',

  alteryx: 'Alteryx',
  apacheAirflow: 'Apache Airflow',
  dbt: 'dbt',
  informatica: 'Informatica',
  matillion: 'Matillion',
  rivery: 'Rivery',
  talend: 'Talend',

  workdayFinancial: 'Workday Financial',
  zuora: 'Zuora',

  concur: 'Concur',
  expensify: 'Expensify',

  fleetio: 'Fleetio',
  geotab: 'Geotab',
  samsara: 'Samsara',

  usingGeneralLedger: 'Using General Ledger',
  acumatica: 'Acumatica',
  epicor: 'Epicor',
  usingFinancialForce: 'FinancialForce',
  usingIntacct: 'Intacct',
  microsoftDynamicsGp: 'Microsoft Dynamics GP',
  usingNetsuite: 'Netsuite',
  oracleFusion: 'Oracle Fusion',
  usingQuickbooks: 'QuickBooks',
  usingQuickbooksOnline: 'QuickBooks Online',
  sapCloudErp: 'SAP Cloud ERP',
  usingXero: 'Xero',

  avalara: 'Avalara',

  usingContainerization: 'Using Containerization',
  docker: 'Docker',
  usingKubernetes: 'Kubernetes',

  sonicwall: 'SonicWall',
  zscaler: 'Zscaler',

  golang: 'Golang',
  usingJava: 'Java',
  javascript: 'JavaScript',
  usingPython: 'Python',
  typescript: 'TypeScript',

  usingCloudflare: 'Cloudflare',
  figma: 'Figma',
  freshworks: 'Freshworks',
  usingGong: 'Gong',
  usingHubSpot: 'HubSpot CRM',
  usingInfor: 'Infor',
  usingJde: 'JDE',
  microservices: 'Microservices',
  outreach: 'Outreach',
  usingWorkday: 'Workday',

  aurora: 'Amazon Aurora',
  influxDb: 'InfluxDB',
  usingMongoDb: 'MongoDB',
  postgres: 'PostgreSQL',
  redis: 'Redis',
  timescale: 'Timescale',

  kql: 'KQL (Kusto Query Language)',
  usingDatadog: 'Datadog',
  usingNewRelic: 'New Relic',
  prometheus: 'Prometheus',
  selenium: 'Selenium',
  usingSplunk: 'Splunk',

  clearbit: 'Clearbit',
  experian: 'Experian',

  qualtrics: 'Qualtrics',
  userTesting: 'UserTesting',
  userZoom: 'UserZoom',

  usingLegacyTech: 'Using Legacy Tech',
  cobol: 'COBOL',
  coldFusion: 'ColdFusion',
  documentum: 'Documentum',
  ektron: 'Ektron',
  ibm: 'IBM',
  lotusNotes: 'Lotus Notes',
  mainframe: 'Mainframe',
  oracle: 'Oracle',
  sap: 'SAP',
  sharepoint: 'SharePoint',
  siebel: 'Siebel',
  sqlServer: 'SQL Server',

  usingLms: 'Using LMS',
  absorbLms: 'Absorb LMS',
  docebo: 'Docebo',
  intellum: 'Intellum',
  learnUpon: 'LearnUpon',
  litmos: 'Litmos',
  skilljar: 'Skilljar',
  talentLms: 'TalentLMS',

  cordial: 'Cordial',
  selligent: 'Selligent',
  zaius: 'Zaius',

  cheetahMail: 'CheetahMail',
  dotmailer: 'Dotmailer',
  silverpop: 'Silverpop',

  bill: 'Bill.com',
  tipalti: 'Tipalti',

  tray: 'Tray.io',
  workato: 'Workato',
  zapier: 'Zapier',

  usingBusinessIntelligence: 'Using Business Intelligence',
  awsQuickSight: 'AWS QuickSight',
  domo: 'Domo',
  googleDataStudio: 'Google Data Studio',
  hex: 'Hex',
  looker: 'Looker',
  metabase: 'Metabase',
  powerBi: 'Microsoft Power BI',
  qlik: 'Qlik',
  redash: 'Redash',
  sisense: 'Sisense',
  tableau: 'Tableau',

  cxOne: 'CXOne',
  five9: 'Five9',
  genesys: 'Genesys',
  livePerson: 'LivePerson',
  twilio: 'Twilio',

  asana: 'Asana',
  confluence: 'Atlassian Confluence',
  jira: 'Atlassian Jira',
  slackApp: 'Slack',

  usingCustomerSuccessPlatform: 'Using Customer Success Platform',
  catalyst: 'Catalyst (CSP)',
  hivebrite: 'Hivebrite',
  planhat: 'Planhat',
  strikedeck: 'Strikedeck',
  vitally: 'Vitally',

  fivetran: 'Fivetran',
  clickHouse: 'ClickHouse',

  stripe: 'Stripe',

  housecallPro: 'Housecall Pro',
  jobber: 'Jobber',
  serviceTitan: 'Service Titan',

  usingMultipleSocialMedia: 'Using Multiple Social Media',
  facebook: 'Facebook',
  twitter: 'X/Twitter',
  instagram: 'Instagram',
  youtubeChannel: 'Youtube Channel',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',

  allbound: 'Allbound',
  channeltivity: 'Channeltivity',
  kiflo: 'Kiflo',
  linkMink: 'LinkMink',
  partnerStack: 'PartnerStack',
  shopifyHeadless: 'Shopify Headless',
  zift: 'Zift',

  drata: 'Drata',
  vanta: 'Vanta',

  safeBase: 'SafeBase',
  secureframe: 'Secureframe',

  fleetPictured: 'Fleet Pictured',

  blotout: 'Blotout',
  blueconic: 'BlueConic',
  kustomer: 'Kustomer',
  lytics: 'Lytics',
  msftDynamics365: 'Microsoft Dynamics 365',
  rudderStack: 'RudderStack',

  noibu: 'Noibu',
  quantumMetric: 'Quantum Metric',

  gladly: 'Gladly',
  monsido: 'Monsido',
  silktide: 'Silktide',

  pagerduty: 'PagerDuty',
  cornerstoneOnDemand: 'Cornerstone OnDemand',

  auth0: 'Auth0',
  cursor: 'Cursor',

  usingCommunitySoftware: 'Using Community Software',
  vanillaForums: 'Higher Logic Vanilla',
  inSided: 'inSided',
  mightyNetworks: 'Mighty Networks',

  usingCustomerFeedback: 'Using Customer Feedback Software',

  usingProductExperienceSoftware: 'Using Product Experience Software',

  capterra: 'Capterra',
  dv360: 'DV360',
  googleAds: 'Google Ads',
  googleAppsForBusiness: 'Google Workspace',
  gusto: 'Gusto',
  hibob: 'Hibob',
  linkedInAds: 'LinkedIn Ads',
  livestorm: 'Livestorm',
  office365Mail: 'Microsoft Office 365',
  okta: 'Okta',
  on24: 'ON24',
  salesforce: 'Salesforce',
  sharpSpring: 'SharpSpring',
  vidyard: 'Vidyard',
  viant: 'Viant',
  webflow: 'Webflow',
  wooCommerce: 'WooCommerce',
  zoom: 'Zoom',
} as const;

// Note: ordering of these signals affects what order client displays
// signals in. Intentionally not ordered alphabetically!
export const ScoringSignalsDefinition = {
  profile: {
    b2bSoftware: 'B2B Software',
    b2bAndB2c: 'B2B and B2C',
    orgDomain: '.Org Domain',

    foundedBefore1950: 'Founded before 1950',
    founded1950to1979: 'Founded 1950 to 1979',
    founded1980to1995: 'Founded 1980 to 1995',
    founded1996to2006: 'Founded 1996 to 2006',
    founded2007to2017: 'Founded 2007 to 2017',
    founded2018orLater: 'Founded 2018 or later',

    // custom
    founded2022orLater: 'Founded 2022 or later',

    // standard employee ranges
    employees1to50: 'Employees 1-50',
    employees51to100: 'Employees 51-100',
    employees101to200: 'Employees 101-200',
    employees201to500: 'Employees 201-500',
    employees501to1000: 'Employees 501-1000',
    employees1001to2500: 'Employees 1001-2500',
    employees2501to5000: 'Employees 2501-5000',
    employeesOver5000: 'Employees 5001+',

    hasFunding: 'Has Funding',
    fundingOver1M: 'Funding: $1M+',
    fundingAmountNotFound: 'Funding: None Found',
    fundingAmount100Kto1M: 'Funding: $100K to $1M',
    fundingAmount1Mto10M: 'Funding: $1M to $10M',
    fundingAmount10Mto50M: 'Funding: $10M to $50M',
    fundingAmount50Mto100M: 'Funding: $50M to $100M',
    fundingAmountOver100M: 'Funding: $100M+',

    lastFundingWithin3Months: 'Last Funding: 0-3 Mos',
    lastFunding4to12Months: 'Last Funding: 4 to 12 Mos',
    lastFunding13to24Months: 'Last Funding: 13 to 24 Mos',
    lastFunding25MonthsPlus: 'Last Funding: 25+ Mos',

    internationalPresence: 'International Presence',
    multipleLocations: 'Multiple Locations',
    publicCompany: 'Public Company',

    ...BusinessAudienceLabels,
    ...PrimaryBusinessCategoryLabels,
    ...SecondaryBusinessCategoryLabels,
  },
  org: {
    hiring: 'Hiring',
    hiringRemote: 'Hiring Remote Roles',
    hiringHybrid: 'Hiring Hybrid Roles',

    hasAbm: 'Has ABM',
    hasAeOrAm: 'Has AE/AM',
    hasAi: 'Has Artificial Intelligence Roles',
    hasCommunity: 'Has Community Roles',
    hasConsumerInsights: 'Has Consumer Insights',
    hasCopywriter: 'Has Copywriter Roles',
    hasCustomerAnalytics: 'Has Customer Analytics',
    hasCustomerExperience: 'Has Customer Experience',
    hasCustomerOperations: 'Has Customer Operations',
    hasCustomerSuccess: 'Has Customer Success',
    hasDataGovernance: 'Has Data Governance',
    hasDataLeader: 'Has Data Leader',
    hasDataLeaderCustom: 'Has Data Leader (short lookback)',
    hasDemandGen: 'Has Demand Gen',
    hasDevExperience: 'Has Dev Experience',
    hasEngineeringLeader: 'Has Engineering Leader',
    hasFinance: 'Has Finance',
    hasHr: 'Has HR',
    hasIt: 'Has IT',
    hasLegal: 'Has Legal',
    hasMachineLearning: 'Has Machine Learning Roles',
    hasMarketingLeader: 'Has Marketing Leader',
    hasProduct: 'Has Product',
    hasProductDesign: 'Has Product Design',
    hasProductManagement: 'Has Product Management',
    hasQa: 'Has QA',
    hasRecruitingOps: 'Has Recruiting Operations',
    hasResearchOps: 'Has Research Ops',
    hasRevSalesOps: 'Has Rev/Sales Ops',
    hasSalesLeader: 'Has Sales Leader',
    hasSdrOrBdr: 'Has SDR/BDR',
    hasSecurity: 'Has Security',
    hasSoftwareEng: 'Has Software Engineering',
    hasUserExperience: 'Has User Experience',

    hiringAbm: 'Hiring ABM',
    hiringAeAm: 'Hiring AE/AM',
    hiringAffiliateMarketing: 'Hiring Affiliate Marketing',
    hiringBrandComms: 'Hiring Brand/Comms',
    hiringBusinessOperations: 'Hiring Business Operations',
    hiringCfdOrCae: 'Hiring CFD or CAE',
    hiringChiefOfStaff: 'Hiring Chief of Staff',
    hiringCommunity: 'Hiring Community',
    hiringCompAndBenefits: 'Hiring Comp & Benefits',
    hiringComplianceOrGrc: 'Hiring Compliance or GRC',
    hiringConsumerInsights: 'Hiring Consumer Insights',
    hiringContentMarketing: 'Hiring Content Marketing',
    hiringCopywriter: 'Hiring Copywriter',
    hiringCustomerAnalytics: 'Hiring Customer Analytics',
    hiringCustomerExperience: 'Hiring Customer Experience',
    hiringCustomerOperations: 'Hiring Customer Operations',
    hiringCustomerSuccess: 'Hiring Customer Success',
    hiringDataGovernance: 'Hiring Data Governance',
    hiringDei: 'Hiring DEI',
    hiringDemandGen: 'Hiring Demand Gen',
    hiringDigitalMarketing: 'Hiring Digital Marketing',
    hiringECommerce: 'Hiring E-Commerce',
    hiringEnterprise: 'Hiring Enterprise',
    hiringEventMarketing: 'Hiring Event Marketing',
    hiringExecutiveOrAdminAssistant: 'Hiring Exec/Admin Assistant',
    hiringFieldMarketing: 'Hiring Field Marketing',
    hiringFieldSales: 'Hiring Field Sales',
    hiringFieldSupport: 'Hiring Field Support',
    hiringFinance: 'Hiring Finance',
    hiringFpna: 'Hiring FP&A',
    hiringGrowth: 'Hiring Growth',
    hiringGtmEng: 'Hiring GTM Engineering',
    hiringHr: 'Hiring HR',
    hiringIt: 'Hiring IT',
    hiringLeadershipAndDevelopment: 'Hiring L&D',
    hiringLegal: 'Hiring Legal',
    hiringMarketing: 'Hiring Marketing',
    hiringMarketingOps: 'Hiring Marketing Ops',
    hiringOmnichannelMarketing: 'Hiring Omnichannel Marketing',
    hiringPartnership: 'Hiring Partnership',
    hiringPeopleTeam: 'Hiring People Team',
    hiringPrivacy: 'Hiring Privacy',
    hiringProductLed: 'Hiring Product Led',
    hiringProductMarketing: 'Hiring Product Marketing',
    hiringProductOps: 'Hiring Product Ops',
    hiringRecruiters: 'Hiring Recruiters',
    hiringRecruitingOps: 'Hiring Recruiting Operations',
    hiringResearchOps: 'Hiring Research Ops',
    hiringRevSalesOps: 'Hiring Rev/Sales Ops',
    hiringSales: 'Hiring Sales',
    hiringSalesEnablement: 'Hiring Sales Enablement',
    hiringSdrBdr: 'Hiring SDR/BDR',
    hiringSecurity: 'Hiring Security',
    hiringSupport: 'Hiring Support',
    hiringSupportOperations: 'Hiring Support Operations',
    hiringSustainabilityRoles: 'Hiring Sustainability Roles',
    hiringTalentAcquisition: 'Hiring Talent Acquisition',
    hiringTechnicalRecruiters: 'Hiring Technical Recruiters',
    hiringWorkforceManagement: 'Hiring Workforce Management',

    // eng roles
    hiringAi: 'Hiring Artificial Intelligence',
    hiringAiOver5: 'Hiring 6+ Artificial Intelligence Roles',
    hiringComputerVision: 'Hiring Computer Vision',
    hiringDataScience: 'Hiring Data Science',
    hiringDataTeam: 'Hiring Data Team',
    hiringDataTeamOver5: 'Hiring 6+ Data Team Roles',
    hiringDesign: 'Hiring Design',
    hiringDetectionEng: 'Hiring Detection Engineering',
    hiringDevExperience: 'Hiring Dev Experience',
    hiringDevOps: 'Hiring Dev Ops',
    hiringDevOpsOver5: 'Hiring 6+ Dev Ops Roles',
    hiringEng: 'Hiring Engineering',
    hiringEngOver10: 'Hiring 11+ Engineering Roles',
    hiringEngineeringManager: 'Hiring Engineering Management',
    hiringGrowthEng: 'Hiring Growth Engineering',
    hiringIntegrationsEng: 'Hiring Integrations Engineering',
    hiringMachineLearning: 'Hiring Machine Learning',
    hiringMobileEng: 'Hiring Mobile Engineering',
    hiringMobileEngOver5: 'Hiring 6+ Mobile Engineering Roles',
    hiringProduct: 'Hiring Product',
    hiringProductDesign: 'Hiring Product Design',
    hiringProductDesignOver5: 'Hiring 6+ Product Design Roles',
    hiringProductManagement: 'Hiring Product Management',
    hiringProgramManagement: 'Hiring Program Management',
    hiringQualityAssurance: 'Hiring QA',
    hiringSalesSolutionsEng: 'Hiring Sales/Solutions Engineering',
    hiringSecurityEng: 'Hiring Security Engineering',
    hiringSoftwareEng: 'Hiring Software Engineering',
    hiringSoftwareEng2to5: 'Hiring 2-5 Software Engineering Roles',
    hiringSoftwareEngOver5: 'Hiring 6+ Software Engineering Roles',
    hiringSoftwareEngIntern: 'Hiring Software Engineering Intern',
    hiringSolutionsEng: 'Hiring Solutions Engineering',
    hiringTechnicalPm: 'Hiring Technical PM',
    hiringUserExperience: 'Hiring User Experience',

    // leadership roles
    hiringCiso: 'Hiring CISO',
    hiringCustomerSuccessLeader: 'Hiring CS Leader',
    hiringDataLeader: 'Hiring Data Leader',
    hiringDesignLeader: 'Hiring Design Leader',
    hiringEngLeader: 'Hiring Engineering Leader',
    hiringFinanceLeader: 'Hiring Finance Leader',
    hiringHrLeader: 'Hiring HR Leader',
    hiringLeaders: 'Hiring Leaders (Dir+)',
    hiringMarketingLeader: 'Hiring Marketing Leader',
    hiringProductLeader: 'Hiring Product Leader',
    hiringSalesLeader: 'Hiring Sales Leader',

    oneMonthGrowthNegative: '1-mo Headcount Growth: Negative',
    oneMonthGrowthNeutral: '1-mo Headcount Growth: Neutral',
    oneMonthGrowthPositive: '1-mo Headcount Growth: Positive',

    sixMonthGrowthNegative: '6-mo Headcount Growth: Negative',
    sixMonthGrowthNeutral: '6-mo Headcount Growth: Neutral',
    sixMonthGrowthPositive: '6-mo Headcount Growth: Positive',
    sixMonthGrowthModerate: '6-mo Headcount Growth: 1% to 10%',
    sixMonthGrowthHigh: '6-mo Headcount Growth: 10% to 20%',
    sixMonthGrowthVeryHigh: '6-mo Headcount Growth: 20%+',

    // custom
    seekingMarketingDecisionMakers: 'Seeking Marketing Decision Makers',
  },
  relevance: {
    aiMessaging: 'AI Messaging',
    genAiMessaging: 'Generative AI Messaging',

    remoteFirstWorkplace: 'Remote First Workplace',
    remoteWorkplace: 'Remote Workplace',
    hybridWorkplace: 'Hybrid Workplace',

    accessibilityPage: 'Accessibility Page',

    liFollowers1to50: 'LI Followers 1-50',
    liFollowers51to500: 'LI Followers 51-500',
    liFollowers501to1000: 'LI Followers 501-1000',
    liFollowers1001to5000: 'LI Followers 1001-5000',
    liFollowersOver5001: 'LI Followers 5001+',
    trafficRankHigh: 'High Website Traffic (Top 1M)',
    trafficRankTop100K: 'Traffic Rank Top 100K',
    trafficRank100Kto200K: 'Traffic Rank 100K-200K',
    trafficRank200Kto300K: 'Traffic Rank 200K-300K',
    trafficRank300Kto500K: 'Traffic Rank 300K-500K',
    trafficRank500Kto1M: 'Traffic Rank 500K-1M',
    recruitingVelocityVeryHigh: 'Recruiting Velocity: Very High',
    recruitingVelocityHigh: 'Recruiting Velocity: High',
    recruitingVelocityModerate: 'Recruiting Velocity: Moderate',
    recruitingVelocityLow: 'Recruiting Velocity: Low',
    recruitingVelocityNone: 'Recruiting Velocity: None',

    community: 'Community',
    discordCommunity: 'Discord Community',
    discourseCommunity: 'Discourse Community',
    otherCommunity: 'Other Community',
    slackCommunity: 'Slack Community',

    linkedInAds1to10: 'LinkedIn Ads: 1-10',
    linkedInAds11to100: 'LinkedIn Ads: 11-100',
    linkedInAds101Plus: 'LinkedIn Ads: 101+',

    // compliance
    jobsMentionCcpa: 'Jobs mention CCPA',
    jobsMentionCisa: 'Jobs mention CISA',
    jobsMentionCra: 'Jobs mention CRA',
    jobsMentionCsf: 'Jobs mention CSF',
    jobsMentionDora: 'Jobs mention DORA',
    jobsMentionFedramp: 'Jobs mention FedRAMP',
    jobsMentionFisma: 'Jobs mention FISMA',
    jobsMentionGdpr: 'Jobs mention GDPR',
    jobsMentionGlba: 'Jobs mention GLBA',
    jobsMentionHipaa: 'Jobs mention HIPAA',
    jobsMentionHitech: 'Jobs mention HITECH',
    jobsMentionHitrust: 'Jobs mention HITRUST',
    jobsMentionIso27001: 'Jobs mention ISO 27001',
    jobsMentionIso42001: 'Jobs mention ISO 42001',
    jobsMentionIso9001: 'Jobs mention ISO 9001',
    jobsMentionNis2: 'Jobs mention NIS2',
    jobsMentionNist: 'Jobs mention NIST',
    jobsMentionNydfs: 'Jobs mention NYDFS',
    jobsMentionPci: 'Jobs mention PCI DSS',
    jobsMentionSoc1: 'Jobs mention SOC 1',
    jobsMentionSoc2: 'Jobs mention SOC 2',
    jobsMentionSox: 'Jobs mention SOX',

    websiteMentionsFedramp: 'Website mentions FedRAMP',
    websiteMentionsGdpr: 'Website mentions GDPR',
    websiteMentionsHipaa: 'Website mentions HIPAA',
    websiteMentionsHitrust: 'Website mentions HITRUST',
    websiteMentionsIso9001: 'Website mentions ISO 9001',
    websiteMentionsIso27001: 'Website mentions ISO 27001',
    websiteMentionsIso42001: 'Website mentions ISO 42001',
    websiteMentionsPciDss: 'Website mentions PCI DSS',
    websiteMentionsSoc1: 'Website mentions SOC 1',
    websiteMentionsSoc2: 'Website mentions SOC 2',

    fedramp: 'FedRAMP',
    hipaa: 'HIPAA',
    hitrust: 'HITRUST',
    iso27001: 'ISO 27001',
    soc1: 'SOC 1',
    soc2: 'SOC 2',
    pciDss: 'PCI DSS',
    iso42001: 'ISO 42001',

    jobsMentionAuth: 'Jobs mention Auth',
    jobsMentionAccessControl: 'Jobs mention RBAC/ABAC',
    jobsMentionAccessManagement: 'Jobs mention Access Management',

    jobsMentionCloudMigration: 'Jobs mention Cloud Migration',
    jobsMentionDigitalTransformation: 'Jobs mention Digital Transformation',

    jobsMentionAbm: 'Jobs mention ABM',
    jobsMentionAbTesting: 'Jobs mention AB Testing',
    jobsMentionCfdOrCae: 'Jobs mention CFD or CAE',
    jobsMentionCisco: 'Jobs mention Cisco',
    jobsMentionDv360: 'Jobs mention DV360',
    jobsMentionContentCreation: 'Jobs mention Content Creation',
    jobsMentionContractor: 'Jobs mention Contractor',
    jobsMentionDei: 'Jobs mention DEI',
    jobsMentionEhr: 'Jobs mention EHR',
    jobsMentionEmployeeTravel: 'Jobs mention Employee Travel',
    jobsMentionEquityComp: 'Jobs mention Equity Comp',
    jobsMentionFpna: 'Jobs mention FP&A',
    jobsMentionHashiCorp: 'Jobs mention HashiCorp',
    jobsMentionHealthStipend: 'Jobs mention Health Stipend',
    jobsMentionIcp: 'Jobs mention ICP',
    jobsMentionIntegrations: 'Jobs mention Integrations',
    jobsMentionInternationalAccounting: 'Jobs mention International Accounting',
    jobsMentionLeadershipAndDevelopment: 'Jobs mention L&D',
    jobsMentionMobile: 'Jobs mention Mobile',
    usingPayEquity: 'Jobs mention Pay Equity',
    jobsMentionProductLed: 'Jobs mention Product Led',
    jobsMentionsQualitativeResearch: 'Jobs mention Qualitative Research',
    retailChannelSales: 'Jobs mention Retail Channel Sales',
    jobsMentionUserResearch: 'Jobs mention User Research',
    jobsMentionVdi: 'Jobs mention VDI',
    jobsMentionViant: 'Jobs mention Viant',
    virtualMachines: 'Jobs mention Virtual Machines',
    jobsMentionVulnerabilityManagement: 'Jobs mention Vulnerability Management',
    jobsMentionWorkforceManagement: 'Jobs mention Workforce Management',
    jobsMentionCaching: 'Jobs mention Caching',
    jobsMentionRealTime: 'Jobs mention Real-Time',

    callCenter: 'Jobs mention Call Center',
    jobsMentionColdCalling: 'Jobs mention Cold Calling',
    phoneSupport: 'Jobs mention Phone Support',

    accessControlPricing: 'RBAC/ABAC Pricing',
    affiliateProgram: 'Affiliate Program',
    agencyProgram: 'Agency Program',
    apiDocs: 'API Docs',
    blogs: 'Blogs',
    buy: 'Buy',
    certification: 'Certification',
    cultureContent: 'Culture Content',
    customerReferralProgram: 'Customer Referral Program',
    deiContent: 'DEI Content',
    economicImpactReport: 'Total Economic Impact Report',
    demo: 'Demo',
    developerPortal: 'Developer Portal',
    downloadOrInstall: 'Download/Install',
    eCommerce: 'E-Commerce',
    emailSignUp: 'Email Sign Up',
    enterprise: 'Enterprise Offer',
    events: 'Events',
    freePlan: 'Free Plan',
    freeTrial: 'Free Trial',
    g2Badge: 'G2 Badge',
    githubPage: 'GitHub Page',
    hasLogin: 'Has Login',
    hasMobileApp: 'Has Mobile App',
    hasPhoneNumber: 'Has Phone Number',
    integrations: 'Integrations',
    multipleIntegrations: 'Multiple Integrations',
    multipleMarkets: 'Multiple Markets',
    multipleProducts: 'Multiple Products',
    newsletter: 'Newsletter',
    onlineReviews: 'Online Reviews',
    openSource: 'Open Source',
    partners: 'Partners',
    platform: 'Platform',
    podcast: 'Podcast',
    productLed: 'Product-Led',
    productTour: 'Product Tour',
    referralProgram: 'Referral Program',
    resellerProgram: 'Reseller Program',
    resources: 'Resources',
    retailChannel: 'Retail Channel',
    salesLed: 'Sales-Led',
    securityPage: 'Security Page',
    servicesCTA: 'Services CTA',
    smsSignUp: 'SMS Sign Up',
    supportChat: 'Support Chat',
    softwareCTA: 'Software CTA',
    solution: 'Solution',
    storeLocator: 'Store Locator',
    templates: 'Templates',
    transparentPricing: 'Transparent Pricing',
    trustCenter: 'Trust Center',
    videoContent: 'Video Content',
    visualProduct: 'Visual Product',
    webinars: 'Webinars',
  },
  stackFit: {
    ...ManualStackFitLabels,
    ...WappAutoSignalLabels,
  },
} as const;

export type KeyplayScoringSignal = AllKeysOf<
  (typeof ScoringSignalsDefinition)[keyof typeof ScoringSignalsDefinition]
>;

export type StackFitSignal = AllKeysOf<
  (typeof ScoringSignalsDefinition)['stackFit']
>;

export type ScoringSignalLabel = AllValuesOf<
  (typeof ScoringSignalsDefinition)[keyof typeof ScoringSignalsDefinition]
>;

export type ScoringCategory = keyof typeof ScoringSignalsDefinition;

export const ScoringCategoryLabels = {
  profile: 'Profile',
  org: 'Org',
  relevance: 'Relevance',
  stackFit: 'Stack',
} as const satisfies Record<ScoringCategory, string>;

export const ScoringCategorySignalLabels = {
  profile: `${ScoringCategoryLabels.profile} Signals`,
  org: `${ScoringCategoryLabels.org} Signals`,
  relevance: `${ScoringCategoryLabels.relevance} Signals`,
  stackFit: `${ScoringCategoryLabels.stackFit} Signals`,
} as const satisfies Record<ScoringCategory, string>;

// signal -> display name
export const ScoringSignalToLabel = <
  Record<KeyplayScoringSignal, ScoringSignalLabel>
>Object.values(ScoringSignalsDefinition).reduce((prev, curr) => ({
  ...prev,
  ...curr,
}));
export const ScoringSignalLabels = Object.values(ScoringSignalToLabel);

// signal -> category
export const ScoringSignalToCategory = <
  Record<KeyplayScoringSignal, ScoringCategory>
>Object.entries(ScoringSignalsDefinition).reduce(
  (prev, [currCategory, currData]) => {
    const currentSignalsToTags: {
      [signal in KeyplayScoringSignal]?: ScoringCategory;
    } = Object.fromEntries(
      Object.keys(currData).map((signal) => [signal, currCategory])
    );
    return {...prev, ...currentSignalsToTags};
  },
  {}
);

export const ScoringCategories = keys(ScoringSignalsDefinition);

export const OrgSignals = keys(ScoringSignalsDefinition['org']);
export const isOrgSignal = typeguardFromArray(OrgSignals);

export const ProfileSignals = keys(ScoringSignalsDefinition['profile']);
export const isProfileSignal = typeguardFromArray(ProfileSignals);

export const RelevanceSignals = keys(ScoringSignalsDefinition['relevance']);
export const isRelevanceSignal = typeguardFromArray(RelevanceSignals);

export const StackFitSignals = keys(ScoringSignalsDefinition['stackFit']);
export const isStackFitSignal = typeguardFromArray(StackFitSignals);

export const KeyplayScoringSignals = [
  ...OrgSignals,
  ...ProfileSignals,
  ...RelevanceSignals,
  ...StackFitSignals,
];
export const isKeyplayScoringSignal = typeguardFromArray(KeyplayScoringSignals);

export const OptionalScoringSignals = [
  'b2bSoftware',
  'servicesCTA',

  // no longer have fresh data for these
  'linkedInAds1to10',
  'linkedInAds11to100',
  'linkedInAds101Plus',

  // Andela
  'hiringAiOver5',
  'hiringDataTeamOver5',
  'hiringDevOpsOver5',
  'hiringProductDesignOver5',
  'hiringMobileEngOver5',
  'hiringEngOver10',

  // Iterable
  'emailSignUp',
  'silodTech',
  'smsSignUp',
  'seekingMarketingDecisionMakers',
  'shopifyHeadless',

  // UserEvidence
  'economicImpactReport',

  // Chainguard
  'fedramp',
  'jobsMentionFedramp',
  'websiteMentionsFedramp',

  // Prismatic
  'jobsMentionIntegrations',

  // Kubit
  'founded2022orLater',
  'hasDataLeaderCustom',

  // Bria
  'computerVisionBriaCustom',

  // Greenhouse
  'hiringTalentAcquisition',

  // Matik
  'jobsMentionContentCreation',
] as const satisfies readonly KeyplayScoringSignal[];

export type OptionalScoringSignal = (typeof OptionalScoringSignals)[number];
export const isOptionalScoringSignal = typeguardFromArray(
  OptionalScoringSignals
);

export const CoreScoringSignals = _.difference(
  KeyplayScoringSignals,
  OptionalScoringSignals
);
export type CoreScoringSignal = StrictExclude<
  KeyplayScoringSignal,
  OptionalScoringSignal
>;
export const isCoreScoringSignal = typeguardFromArray(CoreScoringSignals);

export const CoreProfileSignals = _.difference(
  ProfileSignals,
  OptionalScoringSignals
);
export const CoreOrgSignals = _.difference(OrgSignals, OptionalScoringSignals);
export const CoreRelevanceSignals = _.difference(
  RelevanceSignals,
  OptionalScoringSignals
);
export const CoreStackFitSignals = _.difference(
  StackFitSignals,
  OptionalScoringSignals
);
export type CoreStackFitSignal = Exclude<StackFitSignal, OptionalScoringSignal>;

// signals that aren't available for use in the SAM (usually because they're duplicative)
export const ScoringOnlySignals: KeyplayScoringSignal[] = [
  'b2bSoftware',
  'employees1to50',
  'employees51to100',
  'employees101to200',
  'employees201to500',
  'employees501to1000',
  'employees1001to2500',
  'employees2501to5000',
  'employeesOver5000',
  ...BusinessAudiences,
  ...PrimaryBusinessCategories,
  ...SecondaryBusinessCategories,
];

// zod schemas that we define after everything else, because the type needs to be
// defined first so we aren't able to infer the type from the schema
export const KeyplayScoringCategorySchema = zodEnum(ScoringCategories);

export const KeyplayScoringSignalSchema = zodEnum(KeyplayScoringSignals);
export const StackFitSignalSchema = zodEnum(StackFitSignals);

export const CustomSignalSchema = z.string();
export type CustomSignal = z.infer<typeof CustomSignalSchema>;

// Note: keep the schema and type in sync with each other if we want both accurate
// type hinting and runtime validation. zod doesn't seem to support the workarounds
// described in https://github.com/microsoft/TypeScript/issues/29729
export const ScoringSignalSchema =
  KeyplayScoringSignalSchema.or(CustomSignalSchema);
export type ScoringSignal = KeyplayScoringSignal | (CustomSignal & {});

export type ResolvedScoringSignal = {
  id: ScoringSignal;
  label: string;
  category: ScoringCategory;
  source: 'custom' | 'keyplay';
};
export type ScoringSignalResolver = (
  scoringSignal: ScoringSignal
) => ResolvedScoringSignal | null;

export function resolveScoringSignal(
  scoringSignal: ScoringSignal,
  signalDefinitions: SignalDefinition[]
): ResolvedScoringSignal | null {
  if (isKeyplayScoringSignal(scoringSignal)) {
    return {
      id: scoringSignal,
      label: ScoringSignalToLabel[scoringSignal],
      category: ScoringSignalToCategory[scoringSignal],
      source: 'keyplay',
    };
  } else {
    const customSignal = signalDefinitions.find(
      (signal) => signal.id === scoringSignal
    );

    if (!customSignal) {
      return null;
    }

    return {
      id: customSignal.id,
      label: customSignal.label,
      category: customSignal.category,
      source: 'custom',
    };
  }
}
